import React, { useEffect, useMemo, useState } from "react";
import { api, formatTime, formatCurrency, STATUS_LABELS, STATUS_CLASSES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, Plus, ChartBar, CurrencyCircleDollar } from "@phosphor-icons/react";
import LessonDialog from "@/components/LessonDialog";
import LessonDetailDialog from "@/components/LessonDetailDialog";

const DAYS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function toLocalISO(d) {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 19);
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mobileDay, setMobileDay] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });

  // Calendar hour range from user settings (with midnight wrap support)
  const startHour = user?.calendar_start_hour ?? 7;
  const endHourRaw = user?.calendar_end_hour ?? 22;
  const HOURS = useMemo(() => {
    const count = endHourRaw > startHour ? (endHourRaw - startHour) : (24 - startHour + endHourRaw);
    const n = Math.max(1, count);
    return Array.from({ length: n }, (_, i) => (startHour + i) % 24);
  }, [startHour, endHourRaw]);
  // Convert a real clock hour+minute into the row offset (in hours) within the visible grid
  function rowOffsetMinutes(hour, minute) {
    const normalized = (hour - startHour + 24) % 24;
    return normalized * 60 + minute;
  }

  // If the view wraps past midnight, extend the query range to catch early-hours lessons of the "next day" after Sunday
  const weekEnd = useMemo(() => addDays(weekStart, endHourRaw <= startHour ? 8 : 7), [weekStart, startHour, endHourRaw]);

  async function load() {
    // Mobile month range (union with desktop week)
    const mStart = new Date(mobileDay.getFullYear(), mobileDay.getMonth(), 1);
    mStart.setHours(0, 0, 0, 0);
    const mEnd = new Date(mobileDay.getFullYear(), mobileDay.getMonth() + 1, 1);
    const rangeStart = weekStart < mStart ? weekStart : mStart;
    const rangeEnd = weekEnd > mEnd ? weekEnd : mEnd;
    const [l, s] = await Promise.all([
      api.get("/lessons", { params: { start: toLocalISO(rangeStart), end: toLocalISO(rangeEnd) } }),
      api.get("/students"),
    ]);
    setLessons(l.data);
    setStudents(s.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart, mobileDay]);

  useEffect(() => {
    const h = () => { setPrefill(null); setOpenNew(true); };
    window.addEventListener("tutorpro:newLesson", h);
    return () => window.removeEventListener("tutorpro:newLesson", h);
  }, []);

  function openNewAt(dayIdx, hour) {
    // If this hour is part of the "next day" portion of the view (wrap), advance the day
    const isWrapHour = hour < startHour;
    const start = addDays(weekStart, dayIdx + (isWrapHour ? 1 : 0));
    start.setHours(hour, 0, 0, 0);
    const end = new Date(start);
    end.setHours(hour + 1);
    // keep datetime-local formatted string (no TZ shift)
    const toLocalInput = (d) => {
      const o = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - o).toISOString().slice(0, 16);
    };
    setPrefill({ start_time: toLocalInput(start), end_time: toLocalInput(end) });
    setOpenNew(true);
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const todayStr = new Date().toDateString();

  // --- Mobile daily view state ---
  const mobileDayLessons = useMemo(() => {
    const dayStr = mobileDay.toDateString();
    const wraps = endHourRaw <= startHour;
    return lessons
      .filter((l) => {
        const st = new Date(l.start_time);
        if (st.toDateString() === dayStr) {
          // Early-morning wrap hours belong to previous visual day
          if (wraps && st.getHours() < startHour) return false;
          return true;
        }
        // Next-day wrap hours attributed to previous day
        if (wraps && st.getHours() < startHour) {
          const prev = new Date(st); prev.setDate(prev.getDate() - 1);
          return prev.toDateString() === dayStr;
        }
        return false;
      })
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  }, [lessons, mobileDay, startHour, endHourRaw]);
  const mobileDayLabel = mobileDay.toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "long" });
  const isMobileToday = mobileDay.toDateString() === todayStr;

  // --- Mini month grid (mobile) ---
  // Build a 6-row × 7-col grid starting Monday for the month of mobileDay.
  const monthGrid = useMemo(() => {
    const y = mobileDay.getFullYear();
    const m = mobileDay.getMonth();
    const first = new Date(y, m, 1);
    first.setHours(0, 0, 0, 0);
    // Monday-first: how many leading days to include
    const lead = (first.getDay() + 6) % 7;
    const gridStart = addDays(first, -lead);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

    // Pre-index lessons by dayString → unique student colors
    const wraps = endHourRaw <= startHour;
    const byDayKey = {};
    lessons.forEach((l) => {
      const st = new Date(l.start_time);
      let key;
      if (wraps && st.getHours() < startHour) {
        // attribute to previous day
        const prev = new Date(st); prev.setDate(prev.getDate() - 1);
        key = prev.toDateString();
      } else {
        key = st.toDateString();
      }
      if (!byDayKey[key]) byDayKey[key] = { count: 0, colors: [] };
      byDayKey[key].count += 1;
      const color = l.student_color || "#3A5A40";
      if (!byDayKey[key].colors.includes(color)) byDayKey[key].colors.push(color);
    });
    return cells.map((d) => ({
      date: d,
      key: d.toDateString(),
      inMonth: d.getMonth() === m,
      isToday: d.toDateString() === todayStr,
      lessons: byDayKey[d.toDateString()] || { count: 0, colors: [] },
    }));
  }, [mobileDay, lessons, startHour, endHourRaw, todayStr]);

  const monthLabel = mobileDay.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

  // Mini month summary: total lessons (excluding cancelled_free) and earned amount for the visible month.
  const monthStats = useMemo(() => {
    const y = mobileDay.getFullYear();
    const m = mobileDay.getMonth();
    let lessonCount = 0;
    let earned = 0;
    lessons.forEach((l) => {
      const st = new Date(l.start_time);
      if (st.getFullYear() !== y || st.getMonth() !== m) return;
      if (l.status === "cancelled_free") return;
      lessonCount += 1;
      if (l.status === "completed" || l.status === "cancelled_paid") {
        earned += Number(l.amount || 0);
      }
    });
    return { lessonCount, earned };
  }, [lessons, mobileDay]);

  function shiftMonth(delta) {
    const d = new Date(mobileDay.getFullYear(), mobileDay.getMonth() + delta, 1);
    d.setHours(0, 0, 0, 0);
    // If the month of today matches the new month, land on today; else on the 1st.
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (today.getFullYear() === d.getFullYear() && today.getMonth() === d.getMonth()) {
      setMobileDay(today);
    } else {
      setMobileDay(d);
    }
  }

  function openNewForMobile(hour) {
    const start = new Date(mobileDay); start.setHours(hour, 0, 0, 0);
    const end = new Date(start); end.setHours(hour + 1);
    const toLocalInput = (d) => {
      const o = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - o).toISOString().slice(0, 16);
    };
    setPrefill({ start_time: toLocalInput(start), end_time: toLocalInput(end) });
    setOpenNew(true);
  }

  // group lessons by day index with overlap resolution (side-by-side columns)
  // When view wraps past midnight, lessons in the "next-day" range also belong
  // visually to the previous column.
  const byDay = useMemo(() => {
    const map = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    const wraps = endHourRaw <= startHour;
    lessons.forEach((l) => {
      const st = new Date(l.start_time);
      let diff = Math.floor((st - weekStart) / (1000 * 60 * 60 * 24));
      // If wrapping and this lesson hour is in the "next-day" portion (e.g., 00:30 with startHour=6),
      // attribute it to the previous visual column.
      if (wraps && st.getHours() < startHour) diff -= 1;
      if (diff >= 0 && diff < 7) map[diff].push(l);
    });
    // For each day, compute column/colCount for overlapping lessons
    for (let d = 0; d < 7; d++) {
      const items = map[d].map((l) => ({
        ...l,
        _s: new Date(l.start_time).getTime(),
        _e: new Date(l.end_time).getTime(),
      }));
      items.sort((a, b) => a._s - b._s || a._e - b._e);
      const cols = [];
      items.forEach((it) => {
        let placed = false;
        for (let i = 0; i < cols.length; i++) {
          if (cols[i] <= it._s) { it._col = i; cols[i] = it._e; placed = true; break; }
        }
        if (!placed) { it._col = cols.length; cols.push(it._e); }
      });
      items.forEach((it) => {
        let group = 1;
        items.forEach((other) => {
          if (other === it) return;
          if (other._s < it._e && other._e > it._s) group = Math.max(group, (other._col || 0) + 1, (it._col || 0) + 1);
        });
        it._group = Math.max(group, cols.length);
      });
      map[d] = items;
    }
    return map;
  }, [lessons, weekStart, startHour, endHourRaw]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Takvim</h1>
          <p className="text-muted-foreground text-sm mt-1 hidden md:block">Haftalık ders programınız.</p>
          <p className="text-muted-foreground text-sm mt-1 md:hidden">Günlük ders programınız.</p>
        </div>
        {/* Desktop week nav */}
        <div className="hidden md:flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="calendar-prev-week"><CaretLeft size={16} /></Button>
          <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="calendar-today-button">Bu Hafta</Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="calendar-next-week"><CaretRight size={16} /></Button>
          <Button className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" onClick={() => { setPrefill(null); setOpenNew(true); }} data-testid="calendar-new-lesson-button">
            <Plus size={16} weight="bold" className="mr-1" /> Yeni Ders
          </Button>
        </div>
        {/* Mobile day nav */}
        <div className="flex md:hidden items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMobileDay(addDays(mobileDay, -1))} data-testid="calendar-prev-day"><CaretLeft size={16} /></Button>
          <Button
            variant={isMobileToday ? "default" : "outline"}
            onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setMobileDay(d); }}
            className={isMobileToday ? "bg-[#3A5A40] hover:bg-[#2E4833] text-white" : ""}
            data-testid="calendar-today-button-mobile"
          >
            Bugün
          </Button>
          <Button variant="outline" size="icon" onClick={() => setMobileDay(addDays(mobileDay, 1))} data-testid="calendar-next-day"><CaretRight size={16} /></Button>
          <Button size="icon" className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" onClick={() => { setPrefill(null); setOpenNew(true); }} data-testid="calendar-new-lesson-button-mobile">
            <Plus size={16} weight="bold" />
          </Button>
        </div>
      </div>

      {/* Desktop weekly grid */}
      <div className="hidden md:block bg-white border border-border rounded-lg overflow-hidden">
        {/* header */}
        <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b border-border bg-muted/40">
          <div className="py-2 text-xs text-muted-foreground text-center">Saat</div>
          {days.map((d, i) => {
            const isToday = d.toDateString() === todayStr;
            return (
              <div key={i} className={`py-2 text-center text-xs ${isToday ? "text-[#3A5A40] font-bold" : "text-muted-foreground"}`}>
                <div className="uppercase tracking-wide">{DAYS_TR[i]}</div>
                <div className={`text-sm ${isToday ? "bg-[#3A5A40] text-white inline-block rounded-full w-6 h-6 leading-6 mt-0.5" : "text-foreground"}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {/* grid */}
        <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))] relative">
          {HOURS.map((h) => (
            <React.Fragment key={h}>
              <div className="border-t border-border text-[10px] text-muted-foreground text-center pt-1 h-16">
                {String(h).padStart(2, "0")}:00
              </div>
              {Array.from({ length: 7 }, (_, dayIdx) => (
                <button
                  key={dayIdx + "-" + h}
                  onClick={() => openNewAt(dayIdx, h)}
                  className="border-t border-l border-border hover:bg-[#FAF9F6] transition-colors relative h-16 text-left"
                  data-testid={`cell-${dayIdx}-${h}`}
                />
              ))}
            </React.Fragment>
          ))}
          {/* lessons overlay */}
          {Object.entries(byDay).map(([dayIdx, items]) =>
            items.map((l) => {
              const st = new Date(l.start_time);
              const en = new Date(l.end_time);
              const startMin = rowOffsetMinutes(st.getHours(), st.getMinutes());
              const durMin = Math.max(30, (en - st) / 60000);
              const top = (startMin / 60) * 4; // 4rem per hour
              const height = (durMin / 60) * 4;
              const leftCol = Number(dayIdx);
              const colCount = Math.max(1, l._group || 1);
              const colIdx = l._col || 0;
              return (
                <button
                  key={l.id}
                  onClick={() => setDetail(l)}
                  className={`absolute rounded-md px-2 py-1 text-[11px] text-left border shadow-sm overflow-hidden hover:shadow-md transition-all ${STATUS_CLASSES[l.status]}`}
                  style={{
                    top: `${top}rem`,
                    height: `${height}rem`,
                    left: `calc(56px + ${leftCol} * ((100% - 56px) / 7) + (((100% - 56px) / 7 - 4px) * ${colIdx} / ${colCount}) + 2px)`,
                    width: `calc(((100% - 56px) / 7 - 4px) / ${colCount})`,
                    borderLeft: `3px solid ${l.teacher_color || l.student_color || "#3A5A40"}`,
                  }}
                  data-testid={`lesson-${l.id}`}
                >
                  <div className="font-semibold truncate">{l.student_name}</div>
                  {l.teacher_name && (
                    <div className="truncate text-[10px] opacity-90 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: l.teacher_color || "#3A5A40" }} />
                      {l.teacher_name}
                    </div>
                  )}
                  <div className="truncate opacity-80">{formatTime(l.start_time)}–{formatTime(l.end_time)}</div>
                  <div className="truncate opacity-80">{STATUS_LABELS[l.status]}</div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Mobile mini month calendar */}
      <div className="md:hidden bg-white border border-border rounded-lg overflow-hidden" data-testid="calendar-mobile-month">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border bg-muted/40">
          <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} className="h-8 w-8" data-testid="calendar-mobile-prev-month">
            <CaretLeft size={16} />
          </Button>
          <div className="font-display font-bold text-sm capitalize" data-testid="calendar-mobile-month-label">{monthLabel}</div>
          <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} className="h-8 w-8" data-testid="calendar-mobile-next-month">
            <CaretRight size={16} />
          </Button>
        </div>
        <div
          className="flex items-center justify-between px-3 py-2 border-b border-border bg-gradient-to-r from-[#EDF5EE] to-white text-xs"
          data-testid="calendar-mobile-month-summary"
        >
          <span className="flex items-center gap-1.5 text-[#3A5A40] font-medium">
            <ChartBar size={14} weight="duotone" />
            <b className="font-mono" data-testid="calendar-mobile-month-lessons">{monthStats.lessonCount}</b> ders
          </span>
          <span className="flex items-center gap-1.5 text-[#3A5A40] font-medium">
            <CurrencyCircleDollar size={14} weight="duotone" />
            <b className="font-mono" data-testid="calendar-mobile-month-earned">{formatCurrency(monthStats.earned, user?.currency || "TRY")}</b>
          </span>
        </div>
        <div className="grid grid-cols-7 gap-px bg-border text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
          {DAYS_TR.map((d) => (
            <div key={d} className="bg-white text-center py-1.5">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-border">
          {monthGrid.map((cell) => {
            const isSelected = cell.key === mobileDay.toDateString();
            const hasLessons = cell.lessons.count > 0;
            return (
              <button
                key={cell.key}
                onClick={() => setMobileDay(new Date(cell.date))}
                className={`bg-white relative flex flex-col items-center justify-start gap-1 py-2 min-h-[52px] text-[13px] transition-colors ${
                  !cell.inMonth ? "text-muted-foreground/50" : "text-foreground"
                } ${isSelected ? "!bg-[#3A5A40] !text-white" : cell.isToday ? "bg-[#E8F5E9]" : "active:bg-[#FAF9F6]"}`}
                data-testid={`month-cell-${cell.key}`}
                aria-label={cell.date.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
                aria-pressed={isSelected}
              >
                <span className={`font-medium ${cell.isToday && !isSelected ? "text-[#3A5A40] font-bold" : ""}`}>
                  {cell.date.getDate()}
                </span>
                {hasLessons && (
                  <div className="flex items-center gap-0.5">
                    {cell.lessons.colors.slice(0, 3).map((c, i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: isSelected ? "#FFFFFF" : c }}
                      />
                    ))}
                    {cell.lessons.colors.length > 3 && (
                      <span className={`text-[9px] leading-none ml-0.5 ${isSelected ? "text-white" : "text-muted-foreground"}`}>
                        +{cell.lessons.colors.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile daily view */}
      <div className="md:hidden bg-white border border-border rounded-lg overflow-hidden" data-testid="calendar-mobile-day">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
          <div>
            <div className={`text-xs uppercase tracking-wider ${isMobileToday ? "text-[#3A5A40] font-bold" : "text-muted-foreground"}`}>
              {isMobileToday ? "Bugün" : mobileDay.toLocaleDateString("tr-TR", { weekday: "short" })}
            </div>
            <div className="font-display font-bold text-lg leading-tight capitalize">{mobileDayLabel}</div>
          </div>
          <div className="text-xs text-muted-foreground">
            {mobileDayLessons.length} ders
          </div>
        </div>

        {mobileDayLessons.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="text-sm text-muted-foreground mb-3">Bu gün ders yok.</div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openNewForMobile((user?.calendar_start_hour ?? 9))}
              data-testid="calendar-mobile-add-lesson"
            >
              <Plus size={14} className="mr-1" /> Ders ekle
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {mobileDayLessons.map((l) => (
              <li
                key={l.id}
                className="flex items-stretch gap-3 px-4 py-3 active:bg-[#FAF9F6] cursor-pointer"
                onClick={() => setDetail(l)}
                data-testid={`mobile-lesson-${l.id}`}
              >
                <div className="w-1 rounded-full flex-shrink-0" style={{ background: l.teacher_color || l.student_color || "#3A5A40" }} />
                <div className="min-w-[64px] text-right">
                  <div className="font-mono text-sm font-semibold">{formatTime(l.start_time)}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{formatTime(l.end_time)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.student_name}</div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                    {l.teacher_name && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: l.teacher_color || "#3A5A40" }} />
                        {l.teacher_name}
                      </span>
                    )}
                    {l.teacher_name && (l.subject || "") ? <span>·</span> : null}
                    <span className="truncate">{l.subject || "Ders"}</span>
                  </div>
                </div>
                <span className={`self-start inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${STATUS_CLASSES[l.status] || ""}`}>
                  {STATUS_LABELS[l.status] || l.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <LessonDialog open={openNew} onOpenChange={setOpenNew} students={students} prefill={prefill} onSaved={load} />
      <LessonDetailDialog lesson={detail} onOpenChange={(v) => !v && setDetail(null)} onChanged={load} />
    </div>
  );
}
